import { useState, useEffect, useMemo } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import ReportService from 'services/Report'
import Report from 'components/Report'
import TextInput from 'components/Inputs/TextInput'
import AlertDialog from 'components/AlertDialog'
import { ReportsContainer, Column, Row } from '../styles'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

export default function Reports () {
  const [loadingData, setLoadingData] = useState (true)
  const [downloading, setDownloading] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const [services, setServices] = useState ([])
  const [name, setName] = useState ('')
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const defaultPeriod = useMemo (() => [
    DateTime.now ().startOf ('month').toISODate (),
    DateTime.now ().toISODate ()
  ], [])

  const professionalStatus = [
    {label: 'Ativos', value: 'active'},
    {label: 'Inativos', value: 'inactive'},
    {label: 'Excluídos', value: 'deleted'},
    {label: 'Todos', value: 'all'}
  ]

  const periodFilters = useMemo (() => [
    { type: 'period', name: 'period', label: 'Período', options: defaultPeriod }
  ], [defaultPeriod])

  const professionalStatusFilters = useMemo (() => [
    { type: 'select', name: 'status', label: 'Status', options: professionalStatus }
  ], [professionalStatus])

  const serviceFilters = useMemo (() => [
    { type: 'multi_select', name: 'service', label: 'Serviço', options: services }
  ], [services])

  const notReturnedFilters = useMemo (() => [
    { type: 'period', name: 'visit_period', label: 'Período de última visita', options: defaultPeriod },
    { type: 'period', name: 'check_period', label: 'Período de retorno', options: defaultPeriod }
  ], [defaultPeriod])

  const combinedServicePeriodFilters = useMemo (
    () => [...periodFilters, ...serviceFilters],
    [serviceFilters, periodFilters]
  )

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])
  
  const getData = async () => {
    try {
      const { data: services } = await cutileiApi.get (
        `/businesses/${businessId}/services?status=active&cutilei_services=false`,
        requestConfig
      )

      setServices ([
        { label: 'Todos', value: 'all_services' },
        ...services.map (service => ({
          label: service.name,
          value: service.id
        }))
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoadingData (false)
    }
  }

  const handleDownload = async (name, filters) => {
    try {
      await ReportService.handleDownload (filters, name, setDownloading)
    } catch (error) {
      setErrorMessage (error.message)
    }
  }

  const reports = useMemo (() => [
    { name: 'Avaliações de profissionais', filters: professionalStatusFilters, onDownload: handleDownload },
    { name: 'Avaliações do salão', onDownload: handleDownload },
    { name: 'Clientes aniversariantes', filters: periodFilters, onDownload: handleDownload },
    { name: 'Clientes com créditos e dívidas', onDownload: handleDownload },
    { name: 'Clientes com pacotes adquiridos', onDownload: handleDownload },
    { name: 'Clientes duplicados', onDownload: handleDownload },
    { name: 'Clientes excluídos', filters: periodFilters, onDownload: handleDownload },
    { name: 'Clientes novos', filters: periodFilters, onDownload: handleDownload },
    { name: 'Clientes por serviço', filters: combinedServicePeriodFilters, onDownload: handleDownload },
    { name: 'Clientes que faltaram ou cancelaram', filters: periodFilters, onDownload: handleDownload },
    { name: 'Clientes que fecharam comandas com cortesia', filters: periodFilters, onDownload: handleDownload },
    { name: 'Clientes que não retornaram', filters: notReturnedFilters, onDownload: handleDownload },
    { name: 'Clientes que retornaram', filters: notReturnedFilters, onDownload: handleDownload },
    { name: 'Comandas excluídas', filters: periodFilters, onDownload: handleDownload },
    { name: 'Entradas e saídas de estoque', filters: periodFilters, onDownload: handleDownload },
    { name: 'Produtos em estoque', onDownload: handleDownload },
    { name: 'Profissionais que receberam caixinha', filters: periodFilters, onDownload: handleDownload },
    { name: 'Rendimentos por categoria de serviço', filters: periodFilters, onDownload: handleDownload },
    { name: 'Rendimentos por método de pagamento', filters: periodFilters, onDownload: handleDownload },
    { name: 'Serviços mais vendidos', filters: periodFilters, onDownload: handleDownload },
    { name: 'Suprimentos e sangrias', filters: periodFilters, onDownload: handleDownload },
    { name: 'Todos os clientes', onDownload: handleDownload }
  ], [periodFilters, combinedServicePeriodFilters, defaultPeriod])

  const [filteredReports, setFilteredReports] = useState (reports)

  useEffect (() => {
    setFilteredReports (
      reports.filter (report => (
        report.name.toLowerCase ().includes (name.toLowerCase ())
      ))
    )
  }, [name, reports])

  return (
    <Column>
      <Row style={{marginBottom: 15}}>
        <TextInput
          value={name}
          placeholder='Pesquisar por nome'
          isClearable={true}
          onChange={e => setName (e.target.value)}
          onClear={() => setName ('')}
          containerStyle={{width: 250}}
          inputStyle={{boxShadow: '0px 1px 10px -6px', paddingInline: 12}}
        />
      </Row>
      {loadingData ? (
        <Loading/>
      ) : (
        <ReportsContainer>
          {filteredReports.map ((report, index) => (
            <Report
              key={index}
              name={report.name}
              filters={report.filters}
              downloading={downloading}
              onDownload={report.onDownload}
            />
          ))}
        </ReportsContainer>
      )}
      <AlertDialog
        visible={errorMessage !== null}
        title='Erro'
        message={errorMessage}
        confirmText='Ok'
        onConfirm={() => setErrorMessage (null)}
        onClose={() => setErrorMessage (null)}
        containerStyle={{width: 360}}
      />
    </Column>
  )
}
